import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = 'Bookd - Book Creatives & Talent Instantly',
  description = 'Find and book artists, athletes, musicians, and creatives for your events. The premier talent marketplace.',
  image = 'https://alwaysbookd.com/og-image.png',
  url,
  type = 'website',
  profile = null, // For profile pages
}) => {
  const siteUrl = 'https://alwaysbookd.com';
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  
  // Profile-specific meta
  if (profile) {
    const profileName = profile.screen_name || profile.name || 'Talent';
    const profileCategory = profile.main_category || profile.categories?.[0] || 'Professional';
    const profileTitle = `${profileName} - ${profileCategory} | Bookd`;
    const profileDesc = profile.bio 
      ? `${profile.bio.slice(0, 150)}...` 
      : `Book ${profileName}, a professional ${profileCategory} on Bookd. View portfolio, reviews, and availability.`;
    const profileImage = profile.portfolio_images?.[0] || profile.picture || image;
    
    return (
      <Helmet>
        <title>{profileTitle}</title>
        <meta name="description" content={profileDesc} />
        
        {/* Open Graph */}
        <meta property="og:title" content={profileTitle} />
        <meta property="og:description" content={profileDesc} />
        <meta property="og:image" content={profileImage} />
        <meta property="og:url" content={fullUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="Bookd" />
        
        {/* Profile-specific */}
        <meta property="profile:username" content={profile.screen_name || ''} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={profileTitle} />
        <meta name="twitter:description" content={profileDesc} />
        <meta name="twitter:image" content={profileImage} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": profileName,
            "description": profileDesc,
            "image": profileImage,
            "url": fullUrl,
            "jobTitle": profileCategory,
            ...(profile.location && { "address": { "@type": "PostalAddress", "addressLocality": profile.location } }),
            ...(profile.rating > 0 && {
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": profile.rating,
                "reviewCount": profile.review_count || 0
              }
            })
          })}
        </script>
      </Helmet>
    );
  }
  
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Bookd" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Canonical */}
      <link rel="canonical" href={fullUrl} />
    </Helmet>
  );
};

export default SEO;
